import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DraggableColumn } from "./DraggableColumn";
import { BigPopoverLayout } from "@foodpilot/foods";
import { ColumnConfigMapping } from "src/components/Grid/hooks/columnsExtension/useGridColumns";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";

export type ColumnPositionPopoverProps = {
    onClose: () => void;
    columnConfig: ColumnConfigMapping;
    columnOrder: string[];
    setColumnOrder: (values: string[]) => void;
    toggleVisibility: (columnName: string, visible: boolean) => void;
};

export const ColumnPositionPopover = (props: ColumnPositionPopoverProps) => {
    const { columnConfig, columnOrder, setColumnOrder, toggleVisibility } = props;
    const { t } = useTranslation();

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = columnOrder.indexOf(active.id as string);
            const newIndex = columnOrder.indexOf(over.id as string);

            const newColumnOrder = arrayMove(columnOrder, oldIndex, newIndex);
            setColumnOrder(newColumnOrder);
        }
    };

    const moveColumn = (dragIndex: number, hoverIndex: number) => {
        const newColumnOrder = arrayMove(columnOrder, dragIndex, hoverIndex);
        setColumnOrder(newColumnOrder);
    };

    const renderColumn = (columnName: string, index: number) => {
        if (!columnName.includes("mrt")) {
            const columnConf = columnConfig[columnName];

            if (columnConf === undefined) {
                return null;
            }

            return (
                <DraggableColumn
                    key={columnName}
                    index={index}
                    columnName={columnName}
                    visibility={columnConf.visible}
                    moveColumn={moveColumn}
                    header={columnConf.displayName}
                    toggleVisibility={toggleVisibility}
                />
            );
        }
        return null;
    };

    const sortableItems = columnOrder.filter((columnName) => !columnName.includes("mrt"));

    return (
        <BigPopoverLayout
            headerText={t("grid.table_actions.customize_columns", "Customize columns")}
            onClose={props.onClose}
        >
            <Typography
                variant="body-medium"
                component="div"
                sx={{
                    marginBottom: "24px",
                    display: "block",
                }}
            >
                {t("popover.reordercolumnsinfo")}
            </Typography>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
                    <Stack useFlexGap gap="2px">
                        {columnOrder.map((columnName, index) => renderColumn(columnName, index))}
                    </Stack>
                </SortableContext>
            </DndContext>
        </BigPopoverLayout>
    );
};
